// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get } from './api';
import { ENDPOINTS } from '../utils/constants';
import type { LogicalNode, MonetizationEnvelope } from '../types/monetization';

/**
 * Fetch every logical node for a site. This is where the UI gets the
 * `logicalNodeId`s it feeds to the telemetry API (ADR-0012) — nothing else
 * publishes them, which is the whole reason this service is consumed: the Live
 * view resolves the ids in a plan's optimizer output against this list before
 * asking telemetry for their observations.
 *
 * `asOf` (RFC 3339 UTC) pins the response to the definition and membership
 * revisions effective at that time; it never re-runs the selector.
 */
export const listLogicalNodes = (
  orgId: string,
  siteId: string,
  asOf?: string
): Promise<MonetizationEnvelope<LogicalNode[]>> => {
  const base = ENDPOINTS.MONETIZATION_LOGICAL_NODES(orgId, siteId);
  const url = asOf ? `${base}?asOf=${encodeURIComponent(asOf)}` : base;
  return get<MonetizationEnvelope<LogicalNode[]>>(url);
};

/** Fetch one logical node and its resolved membership snapshot. */
export const getLogicalNode = (
  orgId: string,
  siteId: string,
  logicalNodeId: string
): Promise<MonetizationEnvelope<LogicalNode>> =>
  get<MonetizationEnvelope<LogicalNode>>(
    ENDPOINTS.MONETIZATION_LOGICAL_NODE(orgId, siteId, logicalNodeId)
  );

/** Human label for a node: the key, falling back to the opaque id. */
export function logicalNodeLabel(node: Pick<LogicalNode, 'logicalNodeKey' | 'logicalNodeId'>) {
  return node.logicalNodeKey || node.logicalNodeId;
}

// The aggregated-state read (`…/logical-nodes/{id}/state`) is still relayed by
// the BFF per ADR-0012, but has no frontend caller: the Live view reads its
// curves from the telemetry API, not from a point-in-time aggregate. Add the
// wrapper back when a screen needs it rather than keeping it here unused.
